import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Tv, Radio, PlayCircle, Youtube, Users, Sparkles, Tag, ChevronRight } from "lucide-react";
import PublicLayout from "@/pages/public/PublicLayout";
import DiscoveryFilterBar from "@/pages/public/DiscoveryFilterBar";
import HRail from "@/pages/public/HRail";
import { usePublicCurrency } from "@/contexts/PublicCurrency";

function embedUrl(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]+)/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

function ChannelPlans() {
  const { fmt } = usePublicCurrency();
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/tv/plans").then(r => setPlans(r.data)).catch(() => {}); }, []);
  if (!plans.length) return null;
  return (
    <section className="rounded-2xl p-5 bg-white/5 border-2 border-white/15" data-testid="tv-plans">
      <h2 className="display text-lg font-bold mb-3 flex items-center gap-2 text-white"><Tag size={18} /> Channel Partner Plans</h2>
      <div className="grid sm:grid-cols-3 gap-3">
        {plans.map(p => (
          <div key={p.tier_key} className="rounded-xl p-4 bg-white/10 border border-white/10 text-white">
            <div className="font-black display text-base">{p.name}</div>
            <div className="display text-xl font-black mono mt-1">{p.price_monthly_inr != null ? fmt(p.price_monthly_inr, p.price_monthly_usd) : "Revenue share"}</div>
            <div className="text-xs opacity-70 mt-1">{p.allowed_boards} board(s) · {Math.round(p.storage_bytes / 1073741824)} GB · {p.allow_direct_uploads ? "direct uploads" : "external links"}</div>
            <Link to="/creator/onboard" className="nb-btn text-xs mt-3 w-full justify-center">Start a Channel</Link>
          </div>
        ))}
      </div>
    </section>
  );
}

function ChannelCard({ ch }) {
  return (
    <Link to={`/tv/channel/${ch.creator_id}`} className="min-w-[240px] snap-start rounded-xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/50 transition-colors" data-testid={`tv-channel-card-${ch.creator_id}`}>
      <div className="h-28 bg-gradient-to-br from-indigo-800 to-purple-800 grid place-items-center relative">
        {ch.thumbnail_url ? <img src={ch.thumbnail_url} alt={ch.display_name} className="w-full h-full object-cover" /> : <Tv size={34} className="opacity-70" />}
        {ch.live_count > 0 && <span className="absolute top-2 left-2 nb-badge bg-red-500 text-white text-[10px] flex items-center gap-1"><Radio size={10} /> LIVE</span>}
      </div>
      <div className="p-3 text-white">
        <div className="font-bold text-sm flex items-center gap-1">{ch.display_name}{ch.is_kids_approved && <span className="nb-badge bg-[#86EFAC] text-black text-[9px]">KIDS</span>}</div>
        <div className="text-xs opacity-60">{(ch.boards || []).join(", ") || "All boards"} · {ch.content_count} videos</div>
      </div>
    </Link>
  );
}

function TVInner() {
  const [filters, setFilters] = useState({ country: "", state: "", district: "", board: "", grade: "", subject: "" });
  const [creators, setCreators] = useState([]);
  const [content, setContent] = useState([]);
  const [live, setLive] = useState([]);
  const [active, setActive] = useState(null);
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => { api.get("/tv/live").then(r => setLive(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/tv/creators", { params: { board: filters.board || undefined } }).then(r => setCreators(r.data)).catch(() => {});
  }, [filters.board]);
  useEffect(() => {
    api.get("/tv/content", { params: { board: filters.board || undefined, grade: filters.grade || undefined, subject: filters.subject || undefined } })
      .then(r => setContent(r.data)).catch(() => {});
  }, [filters.board, filters.grade, filters.subject]);

  // Auto-advancing hero across the top channels.
  const spotlight = creators.slice(0, 5);
  useEffect(() => {
    if (spotlight.length < 2) return;
    const t = setInterval(() => setHeroIdx(i => (i + 1) % spotlight.length), 5000);
    return () => clearInterval(t);
  }, [spotlight.length]);
  const hero = spotlight[heroIdx % (spotlight.length || 1)];
  const recent = [...creators].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 12);

  return (
    <>
      <DiscoveryFilterBar value={filters} onChange={setFilters} theme="dark" />
      <main className="max-w-6xl mx-auto p-4 space-y-8">
        {/* Hero billboard */}
        {hero ? (
          <div className="relative rounded-2xl overflow-hidden border-2 border-white/20 min-h-[220px] flex items-end p-6 bg-gradient-to-r from-indigo-900 via-purple-900 to-fuchsia-900" data-testid="tv-billboard">
            <div className="absolute inset-0 opacity-30" style={hero.thumbnail_url ? { backgroundImage: `url(${hero.thumbnail_url})`, backgroundSize: "cover", backgroundPosition: "center" } : {}} />
            <div className="relative z-10">
              <div className="text-xs opacity-70 mono flex items-center gap-2"><Sparkles size={13} /> FEATURED CHANNEL</div>
              <h1 className="display text-3xl md:text-5xl font-black mt-1">{hero.display_name}</h1>
              <div className="text-sm opacity-80">{hero.organization || "Ponderr Creator"} · {(hero.boards || []).join(", ")} · {hero.content_count} lessons</div>
              <Link to={`/tv/channel/${hero.creator_id}`} className="nb-btn mt-4" data-testid="tv-watch-now">▶ Watch Now</Link>
            </div>
            {spotlight.length > 1 && (
              <div className="absolute bottom-4 right-4 z-10 flex gap-1.5">
                {spotlight.map((_, i) => <button key={i} onClick={() => setHeroIdx(i)} className={`w-2.5 h-2.5 rounded-full ${i === heroIdx % spotlight.length ? "bg-white" : "bg-white/30"}`} aria-label={`slide ${i}`} />)}
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-2xl border-2 border-white/20 p-8 text-center text-white/60" data-testid="tv-billboard-empty">No channels published yet — approved creator channels appear here.</div>
        )}

        <HRail title="Trending Channels" icon={Tv} dark empty={!creators.length && <div className="text-white/50 text-sm py-4">No channels yet.</div>}>
          {creators.map(ch => <ChannelCard key={ch.creator_id} ch={ch} />)}
        </HRail>

        <HRail title="Recently Joined Creators" icon={Users} dark empty={!recent.length && <div className="text-white/50 text-sm py-4">No creators yet.</div>}>
          {recent.map(ch => <ChannelCard key={`r-${ch.creator_id}`} ch={ch} />)}
        </HRail>

        <HRail title="Free Lessons & Doubt-Clearing Broadcasts" icon={Radio} dark empty={!live.length && <div className="text-white/50 text-sm py-4">No live broadcasts scheduled.</div>}>
          {live.map(s => (
            <a key={s.session_id} href={s.meeting_url || "#"} target="_blank" rel="noreferrer" className="min-w-[230px] snap-start rounded-xl p-4 bg-white/5 border border-white/10 hover:border-white/40 text-white" data-testid={`tv-live-${s.session_id}`}>
              <div className="text-xs mono opacity-60 flex items-center gap-1"><Radio size={12} className="text-red-400" /> {s.platform}</div>
              <div className="font-bold text-sm mt-1">{s.title}</div>
              <div className="text-xs opacity-70">{s.board} {s.grade} · {s.subject}</div>
              <div className="nb-badge bg-white text-black text-[10px] mt-2 inline-flex items-center gap-1">Join <ChevronRight size={11} /></div>
            </a>
          ))}
        </HRail>

        <ChannelPlans />

        {/* Content grid */}
        <section className="space-y-3">
          <h2 className="display text-lg font-bold text-white">Browse Lessons</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {content.map(c => (
              <button key={c.content_id} onClick={() => setActive(c)} className="text-left rounded-xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/40 transition-colors" data-testid={`tv-content-${c.content_id}`}>
                <div className="h-32 bg-black/40 grid place-items-center relative">
                  {c.thumbnail_url ? <img src={c.thumbnail_url} alt={c.title} className="w-full h-full object-cover" /> : <PlayCircle size={40} className="opacity-50 text-white" />}
                </div>
                <div className="p-3 text-white">
                  <div className="font-bold text-sm line-clamp-2">{c.title}</div>
                  <div className="text-xs opacity-60 mt-1">{c.board} {c.grade} · {c.subject}</div>
                </div>
              </button>
            ))}
            {content.length === 0 && <div className="col-span-full text-center py-10 text-white/50">No published lessons for this filter yet.</div>}
          </div>
        </section>
      </main>

      {active && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4" onClick={() => setActive(null)}>
          <div className="max-w-3xl w-full space-y-3" onClick={e => e.stopPropagation()} data-testid="tv-player">
            <div className="aspect-video bg-black rounded-xl overflow-hidden border-2 border-white/20">
              {embedUrl(active.external_url)
                ? <iframe title={active.title} src={embedUrl(active.external_url)} className="w-full h-full" allowFullScreen />
                : active.media_url ? <video src={active.media_url} controls className="w-full h-full" />
                  : <div className="grid place-items-center h-full opacity-60 text-white"><Youtube size={48} /></div>}
            </div>
            <div className="flex items-center justify-between text-white">
              <div><div className="display text-lg font-bold">{active.title}</div>
                <div className="text-xs opacity-70">{active.board} {active.grade} · {active.subject}</div></div>
              <button className="nb-btn nb-btn-danger" onClick={() => setActive(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function LiveTV() {
  return (
    <PublicLayout theme="dark">
      <TVInner />
    </PublicLayout>
  );
}
